import React, { useEffect, useState } from "react";
import { Container, Table, Modal, Button } from "react-bootstrap";
import PaginationComponent from "../component/Pagination";
import Loader from "../component/Loader";
import Swal from "sweetalert2";
import Select from "react-select";
import { useData, US_STATES } from "../context/DataContext";

import LogoSvg from "../Assets/images/logo.svg";
import styles from "../Assets/css/fleet.module.css";

const FranklinUnifiedDetails = () => {
  const { utilities: contextUtilities, fetchAllData } = useData();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const FRANKLIN_URL = BASE_URL + "devices/unified-list/detail/?type=Franklin";

  const [deviceData, setDeviceData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Utility and State Filters
  const [selectedUtility, setSelectedUtility] = useState("All");
  const [selectedState, setSelectedState] = useState("All");

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);

  // Parameter states
  const [deviceId, setDeviceId] = useState("");
  const [model, setModel] = useState("");
  const [electricCompany, setElectricCompany] = useState("");
  const [nonWorkingDaysEnabled, setNonWorkingDaysEnabled] = useState("");
  const [cmd, setCmd] = useState("");
  const [runingMode, setRuningMode] = useState("");
  const [stormEn, setStormEn] = useState("");
  const [batFd, setBatFd] = useState("");
  const [solarFd, setSolarFd] = useState("");
  const [gridCh, setGridCh] = useState("");
  const [eventList, setEventList] = useState("");
  const [packageState, setPackageState] = useState("");
  const [packageArea, setPackageArea] = useState("");
  const [eleCompany, setEleCompany] = useState("");
  const [packageType, setPackageType] = useState("");
  const [modeChoose, setModeChoose] = useState("");
  const [defaultMode, setDefaultMode] = useState("");
  const [solarEnable, setSolarEnable] = useState("");
  const [complianceRuleType, setComplianceRuleType] = useState("");
  const [groupId, setGroupId] = useState("");
  const [setType, setSetType] = useState("");

  const [loading, setLoading] = useState({
    global: false,
    filter: false,
  });

  const postsPerPage = 10;

  // Calculate yesterday's date
  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  // ------------------------------
  // DATE + TIME FILTER STATES
  // ------------------------------
  const [startDate, setStartDate] = useState(getYesterdayDate());
  const [startTime, setStartTime] = useState("00:00");

  const [endDate, setEndDate] = useState(getYesterdayDate());
  const [endTime, setEndTime] = useState("23:00");

  const [dateError, setDateError] = useState("");

  // ------------------------------
  // FETCH FRANKLIN DATA
  // ------------------------------
  const fetchFranklinData = async (start = "", end = "", utility = "All", state = "All") => {
    try {
      setLoading((prev) => ({ ...prev, global: true }));

      const storedAccessToken = localStorage.getItem("accessToken");
      if (!storedAccessToken) throw new Error("Access token missing");

      let apiURL = FRANKLIN_URL;
      const params = [];
      
      if (start && end) {
        params.push(`startdate=${start}`);
        params.push(`enddate=${end}`);
      }
      if (utility && utility !== "All") {
        params.push(`utility=${encodeURIComponent(utility)}`);
      }
      if (state && state !== "All") {
        params.push(`state=${encodeURIComponent(state)}`);
      }
      
      if (params.length > 0) {
        apiURL = `${FRANKLIN_URL}&${params.join('&')}`;
      }

      const response = await fetch(apiURL, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${storedAccessToken}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch data");

      const data = await response.json();
      setDeviceData(data.results || []);
      setFilteredData(data.results || []);
    } catch (error) {
      console.error("Franklin Fetch Error:", error);
      setDeviceData([]);
      setFilteredData([]);
    } finally {
      setLoading((prev) => ({ ...prev, global: false }));
    }
  };

  // ------------------------------
  // INITIAL LOAD
  // ------------------------------
  useEffect(() => {
    fetchFranklinData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch utilities if not already loaded in context
  useEffect(() => {
    const storedAccessToken = localStorage.getItem('accessToken');
    if (storedAccessToken && !contextUtilities) {
      fetchAllData(storedAccessToken);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------
  // APPLY FILTER
  // ------------------------------
  const handleFilter = async () => {
    if (!startDate || !endDate) {
      setDateError("Please select Start Date and End Date and Hour");
      return;
    }

    setDateError("");

    const startValue = `${startDate.replace(/-/g, '/')}T${startTime || "00:00"}:00`;
    const endValue = `${endDate.replace(/-/g, '/')}T${endTime || "23:00"}:00`;

    setLoading((prev) => ({ ...prev, filter: true }));

    await fetchFranklinData(startValue, endValue, selectedUtility, selectedState);
    setCurrentPage(1);

    setLoading((prev) => ({ ...prev, filter: false }));
  };

  // ------------------------------
  // CLEAR FILTER
  // ------------------------------
  const clearFilter = () => {
    setStartDate("");
    setEndDate("");
    setStartTime("");
    setEndTime("");
    setSelectedUtility("All");
    setSelectedState("All");
    setDateError("");

    fetchFranklinData();
  };

  // ------------------------------
  // MODAL HANDLERS
  // ------------------------------
  const handleOpenModal = (device) => {
    setSelectedDevice(device);
    setShowModal(true);
    setDeviceId(device.device_data?.deviceId || device.device_id || "");
    // Reset all fields
    setModel("");
    setElectricCompany("");
    setNonWorkingDaysEnabled("");
    setCmd("");
    setRuningMode("");
    setStormEn("");
    setBatFd("");
    setSolarFd("");
    setGridCh("");
    setEventList("");
    setPackageState("");
    setPackageArea("");
    setEleCompany("");
    setPackageType("");
    setModeChoose("");
    setDefaultMode("");
    setSolarEnable("");
    setComplianceRuleType("");
    setGroupId("");
    setSetType("");
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDevice(null);
  };

  // ------------------------------
  // COMMAND HANDLERS
  // ------------------------------
  const sendCommand = async (slug, additionalParams = {}) => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        Swal.fire({ icon: "error", title: "Error", text: "Access token missing" });
        return;
      }

      const payload = {
        device_id: deviceId,
        device_type: "franklin",
        slug: slug,
        deviceId: deviceId,
        ...additionalParams,
      };

      const response = await fetch(BASE_URL + "devices/send-command/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to send command");

      const result = await response.json();
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Command executed successfully!",
        timer: 2000,
      });
    } catch (error) {
      console.error("Command Error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to execute command. Please try again.",
      });
    }
  };

  const handleSetTouParameters = () => {
    if (!deviceId || !model || !electricCompany || !nonWorkingDaysEnabled) {
      Swal.fire({ icon: "warning", title: "Required", text: "All TOU fields are required" });
      return;
    }
    sendCommand("set-tou-parameters", { model, electricCompany, nonWorkingDaysEnabled });
  };

  const handleBatterySetSwitchParam = () => {
    if (!electricCompany || !cmd) {
      Swal.fire({ icon: "warning", title: "Required", text: "Electric Company and Command are required" });
      return;
    }
    sendCommand("battery-set-switch-param", { electricCompany, cmd });
  };

  const handleSetSmartCircuits = () => {
    if (!electricCompany) {
      Swal.fire({ icon: "warning", title: "Required", text: "Electric Company is required" });
      return;
    }
    sendCommand("set-smart-circuits", { electricCompany });
  };

  const handleSetEnergyManagement = () => {
    if (!deviceId || !runingMode || !stormEn || !batFd || !solarFd || !gridCh) {
      Swal.fire({ icon: "warning", title: "Required", text: "All energy management fields are required" });
      return;
    }
    sendCommand("set-energy-management-parameters", { runingMode, stormEn, batFd, solarFd, gridCh });
  };

  const handleSetGridEvents = () => {
    if (!deviceId || !eventList) {
      Swal.fire({ icon: "warning", title: "Required", text: "Device ID and Event List are required" });
      return;
    }
    sendCommand("set-grid-events", { eventList });
  };

  const handleSetGridPackage = () => {
    if (!deviceId || !packageState || !packageArea || !eleCompany || !packageType || !modeChoose || !defaultMode || !solarEnable) {
      Swal.fire({ icon: "warning", title: "Required", text: "All grid package fields are required" });
      return;
    }
    sendCommand("set-grid-package", { packageState, packageArea, eleCompany, packageType, modeChoose, defaultMode, solarEnable });
  };

  const handleSetGridCompliance = () => {
    if (!deviceId || !complianceRuleType) {
      Swal.fire({ icon: "warning", title: "Required", text: "Device ID and Compliance Rule Type are required" });
      return;
    }
    sendCommand("set-grid-interconnection-compliance", { ComplianceRuleType: complianceRuleType });
  };

  const handleSetDevicesByGroup = () => {
    if (!groupId || !setType) {
      Swal.fire({ icon: "warning", title: "Required", text: "Group ID and Set Type are required" });
      return;
    }
    sendCommand("set-devices-by-group", { groupId, setType });
  };

  // ------------------------------
  // PAGINATION
  // ------------------------------
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredData.slice(indexOfFirstPost, indexOfLastPost);

  return (
    <div className={styles.fleet_view_cantaint}>
      {loading.global && <Loader />}

      {/* HEADER */}
      <section className={styles.topbar}>
        <div className={styles.container}>
          <img className={styles.topbar_logo} src={LogoSvg} alt="Topbar Logo" />
        </div>
      </section>

      <section className="thermostat-details electric-vehicle-details">
        <Container>
          <h2 className={styles.main_title}>Franklin Details</h2>

          {/* -------------------- FILTER UI -------------------- */}
          <div className="row g-2 mt-3 mb-2 align-items-end">
            {/* Start Date */}
            <div className="col-6 col-md-3">
              <label className="small">Start Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            {/* Start Time */}
            <div className="col-6 col-md-2">
              <label className="small">Start Hour</label>
              <select
                className="form-control form-control-sm"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              >
                <option value="">Hr</option>
                {Array.from({ length: 24 }).map((_, i) => {
                  const hour = i.toString().padStart(2, "0");
                  return (
                    <option key={i} value={`${hour}:00`}>
                      {hour}:00
                    </option>
                  );
                })}
              </select>
            </div>

            {/* End Date */}
            <div className="col-6 col-md-3">
              <label className="small">End Date</label>
              <input
                type="date"
                min={startDate}
                className="form-control form-control-sm"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            {/* End Time */}
            <div className="col-6 col-md-2">
              <label className="small">End Hour</label>
              <select
                className="form-control form-control-sm"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              >
                <option value="">Hr</option>
                {Array.from({ length: 24 }).map((_, i) => {
                  const hour = i.toString().padStart(2, "0");
                  return (
                    <option key={i} value={`${hour}:00`}>
                      {hour}:00
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Utility Filter */}
            <div className="col-6 col-md-4">
              <label className="small">Utility</label>
              <Select
                options={[
                  { value: "All", label: "All Utility" },
                  ...(contextUtilities || []).map((utility) => ({
                    value: utility.id.toString(),
                    label: utility.utility_name,
                  })),
                ]}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    backgroundColor: "#fff",
                    borderColor: "#b6b6b6",
                    minHeight: "31px",
                    height: "31px",
                    boxShadow: "none",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#4c2806",
                    borderRadius: "3px",
                    cursor: "pointer"
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    height: "31px",
                    padding: "0 6px",
                    display: "flex",
                    alignItems: "center"
                  }),
                  singleValue: (provided) => ({
                    ...provided,
                    color: "#4c2806",
                    position: "relative",
                    top: "auto",
                    transform: "none",
                    margin: 0,
                    maxWidth: "100%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }),
                  input: (provided) => ({
                    ...provided,
                    margin: "0px",
                    padding: "0px"
                  }),
                  indicatorSeparator: () => ({
                    display: "none",
                  }),
                  indicatorsContainer: (provided) => ({
                    ...provided,
                    height: "31px",
                  }),
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                    fontSize: "13px",
                    color: "#4c2806"
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected ? "#ffe0b2" : state.isFocused ? "#fff7e9" : "white",
                    color: "#4c2806",
                    cursor: "pointer"
                  })
                }}
                value={
                  selectedUtility === "All"
                    ? { value: "All", label: "All Utility" }
                    : (contextUtilities || []).map(u => ({ value: u.id.toString(), label: u.utility_name })).find(opt => opt.value === selectedUtility)
                }
                onChange={(option) => setSelectedUtility(option ? option.value : "All")}
                placeholder="Select Utility"
                isSearchable
                isClearable
              />
            </div>

            {/* State Filter */}
            <div className="col-6 col-md-4">
              <label className="small">State</label>
              <Select
                options={[
                  { value: "All", label: "All States" },
                  ...US_STATES.map((state) => ({ value: state, label: state })),
                ]}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    backgroundColor: "#fff",
                    borderColor: "#b6b6b6",
                    minHeight: "31px",
                    height: "31px",
                    boxShadow: "none",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#4c2806",
                    borderRadius: "3px",
                    cursor: "pointer"
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    height: "31px",
                    padding: "0 6px",
                    display: "flex",
                    alignItems: "center"
                  }),
                  singleValue: (provided) => ({
                    ...provided,
                    color: "#4c2806",
                    position: "relative",
                    top: "auto",
                    transform: "none",
                    margin: 0,
                    maxWidth: "100%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }),
                  input: (provided) => ({
                    ...provided,
                    margin: "0px",
                    padding: "0px"
                  }),
                  indicatorSeparator: () => ({
                    display: "none",
                  }),
                  indicatorsContainer: (provided) => ({
                    ...provided,
                    height: "31px",
                  }),
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                    fontSize: "13px",
                    color: "#4c2806"
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected ? "#ffe0b2" : state.isFocused ? "#fff7e9" : "white",
                    color: "#4c2806",
                    cursor: "pointer"
                  })
                }}
                value={
                  selectedState === "All"
                    ? { value: "All", label: "All States" }
                    : { value: selectedState, label: selectedState }
                }
                onChange={(option) => setSelectedState(option ? option.value : "All")}
                placeholder="Select State"
                isSearchable
                isClearable
              />
            </div>

            {/* Buttons */}
            <div className="col-12 col-md-4 d-flex gap-2 mt-md-0 mt-2">
              <button
                className="btn btn-sm filter-button"
                style={{ backgroundColor: "#ff5900", color: "white", border: "none" }}
                onClick={handleFilter}
              >
                Filter
              </button>

              <button
                className="btn btn-secondary btn-sm filter-button"
                onClick={clearFilter}
              >
                Clear
              </button>
            </div>
          </div>

          {dateError && <div style={{ color: "red" }}>{dateError}</div>}

          {/* -------------------- TABLE -------------------- */}
          <div className="table-responsive mt-4">
            <Table className={styles.table}>
              <thead>
                <tr>
                  <th>Email</th>
                  <th className="text-center">Device ID</th>
                  <th className="text-center">Phone Number</th>
                  <th className="text-center">Production (kWh)</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {currentPosts.length > 0 ? (
                  currentPosts.map((device, index) => (
                    <tr key={index}>
                      <td>{device.email || "N/A"}</td>
                      <td className="text-center">{device.device_id}</td>
                      <td className="text-center">{device.phone_number || "N/A"}</td>
                      <td className="text-center">{device.total_production}</td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm"
                          style={{ backgroundColor: "#ff5900", color: "white", border: "none" }}
                          onClick={() => handleOpenModal(device)}
                        >
                          Commands
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-4">
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <PaginationComponent
            currentPage={currentPage}
            totalPages={Math.ceil(filteredData.length / postsPerPage)}
            onPageChange={setCurrentPage}
          />
        </Container>
      </section>

      {/* -------------------- COMMANDS MODAL -------------------- */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Franklin Device Commands</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDevice && (
            <div>
              <p><strong>Email:</strong> {selectedDevice.email}</p>
              <p><strong>Device ID:</strong> {selectedDevice.device_id}</p>

              {/* Device ID (read-only, pre-filled) */}
              <div className="mb-3">
                <label className="form-label">Device ID <span style={{ color: "red" }}>*</span></label>
                <input type="text" className="form-control" value={deviceId} readOnly disabled />
              </div>

              {/* TOU Parameters */}
              <h6 className="mt-4 mb-3">TOU Parameters</h6>
              <div className="row g-2 mb-3">
                <div className="col-4">
                  <label className="small">Model <span style={{ color: "red" }}>*</span></label>
                  <input type="text" className="form-control form-control-sm" value={model} onChange={(e) => setModel(e.target.value)} />
                </div>
                <div className="col-4">
                  <label className="small">Electric Company <span style={{ color: "red" }}>*</span></label>
                  <input type="text" className="form-control form-control-sm" value={electricCompany} onChange={(e) => setElectricCompany(e.target.value)} />
                </div>
                <div className="col-4">
                  <label className="small">Non-Working Days <span style={{ color: "red" }}>*</span></label>
                  <input type="text" className="form-control form-control-sm" value={nonWorkingDaysEnabled} onChange={(e) => setNonWorkingDaysEnabled(e.target.value)} />
                </div>
              </div>
              <Button variant="primary" className="w-100 mb-3" onClick={handleSetTouParameters}>Set TOU Parameters</Button>

              {/* Battery Switch */}
              <h6 className="mt-4 mb-3">Battery Switch</h6>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="small">Electric Company <span style={{ color: "red" }}>*</span></label>
                  <input type="text" className="form-control form-control-sm" value={electricCompany} onChange={(e) => setElectricCompany(e.target.value)} />
                </div>
                <div className="col-6">
                  <label className="small">Command <span style={{ color: "red" }}>*</span></label>
                  <input type="text" className="form-control form-control-sm" value={cmd} onChange={(e) => setCmd(e.target.value)} />
                </div>
              </div>
              <Button variant="warning" className="w-100 mb-3" onClick={handleBatterySetSwitchParam}>Set Battery Switch</Button>

              {/* Smart Circuits */}
              <h6 className="mt-4 mb-3">Smart Circuits</h6>
              <div className="mb-3">
                <label className="small">Electric Company <span style={{ color: "red" }}>*</span></label>
                <input type="text" className="form-control form-control-sm" value={electricCompany} onChange={(e) => setElectricCompany(e.target.value)} />
              </div>
              <Button variant="info" className="w-100 mb-3" onClick={handleSetSmartCircuits}>Set Smart Circuits</Button>

              {/* Energy Management */}
              <h6 className="mt-4 mb-3">Energy Management</h6>
              <div className="row g-2 mb-3">
                <div className="col-4">
                  <label className="small">Running Mode <span style={{ color: "red" }}>*</span></label>
                  <input type="text" className="form-control form-control-sm" value={runingMode} onChange={(e) => setRuningMode(e.target.value)} />
                </div>
                <div className="col-4">
                  <label className="small">Storm Enable <span style={{ color: "red" }}>*</span></label>
                  <input type="text" className="form-control form-control-sm" value={stormEn} onChange={(e) => setStormEn(e.target.value)} />
                </div>
                <div className="col-4">
                  <label className="small">Battery Feed <span style={{ color: "red" }}>*</span></label>
                  <input type="text" className="form-control form-control-sm" value={batFd} onChange={(e) => setBatFd(e.target.value)} />
                </div>
                <div className="col-4">
                  <label className="small">Solar Feed <span style={{ color: "red" }}>*</span></label>
                  <input type="text" className="form-control form-control-sm" value={solarFd} onChange={(e) => setSolarFd(e.target.value)} />
                </div>
                <div className="col-4">
                  <label className="small">Grid Charge <span style={{ color: "red" }}>*</span></label>
                  <input type="text" className="form-control form-control-sm" value={gridCh} onChange={(e) => setGridCh(e.target.value)} />
                </div>
              </div>
              <Button variant="success" className="w-100 mb-3" onClick={handleSetEnergyManagement}>Set Energy Management</Button>

              {/* Grid Events */}
              <h6 className="mt-4 mb-3">Grid Events</h6>
              <div className="mb-3">
                <label className="small">Event List (JSON) <span style={{ color: "red" }}>*</span></label>
                <textarea className="form-control" rows="3" value={eventList} onChange={(e) => setEventList(e.target.value)} placeholder='[{"event": "value"}]' />
              </div>
              <Button variant="dark" className="w-100 mb-3" onClick={handleSetGridEvents}>Set Grid Events</Button>

              {/* Grid Package */}
              <h6 className="mt-4 mb-3">Grid Package</h6>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="small">Package State <span style={{ color: "red" }}>*</span></label>
                  <input type="text" className="form-control form-control-sm" value={packageState} onChange={(e) => setPackageState(e.target.value)} />
                </div>
                <div className="col-6">
                  <label className="small">Package Area <span style={{ color: "red" }}>*</span></label>
                  <input type="text" className="form-control form-control-sm" value={packageArea} onChange={(e) => setPackageArea(e.target.value)} />
                </div>
                <div className="col-6">
                  <label className="small">Electric Company <span style={{ color: "red" }}>*</span></label>
                  <input type="text" className="form-control form-control-sm" value={eleCompany} onChange={(e) => setEleCompany(e.target.value)} />
                </div>
                <div className="col-6">
                  <label className="small">Package Type <span style={{ color: "red" }}>*</span></label>
                  <input type="text" className="form-control form-control-sm" value={packageType} onChange={(e) => setPackageType(e.target.value)} />
                </div>
                <div className="col-6">
                  <label className="small">Mode Choose <span style={{ color: "red" }}>*</span></label>
                  <input type="text" className="form-control form-control-sm" value={modeChoose} onChange={(e) => setModeChoose(e.target.value)} />
                </div>
                <div className="col-6">
                  <label className="small">Default Mode <span style={{ color: "red" }}>*</span></label>
                  <input type="text" className="form-control form-control-sm" value={defaultMode} onChange={(e) => setDefaultMode(e.target.value)} />
                </div>
                <div className="col-6">
                  <label className="small">Solar Enable <span style={{ color: "red" }}>*</span></label>
                  <input type="text" className="form-control form-control-sm" value={solarEnable} onChange={(e) => setSolarEnable(e.target.value)} />
                </div>
              </div>
              <Button variant="primary" className="w-100 mb-3" onClick={handleSetGridPackage}>Set Grid Package</Button>

              {/* Grid Compliance */}
              <h6 className="mt-4 mb-3">Grid Interconnection Compliance</h6>
              <div className="mb-3">
                <label className="small">Compliance Rule Type <span style={{ color: "red" }}>*</span></label>
                <input type="text" className="form-control form-control-sm" value={complianceRuleType} onChange={(e) => setComplianceRuleType(e.target.value)} />
              </div>
              <Button variant="danger" className="w-100 mb-3" onClick={handleSetGridCompliance}>Set Grid Compliance</Button>

              {/* Group Settings */}
              <h6 className="mt-4 mb-3">Group Settings</h6>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="small">Group ID <span style={{ color: "red" }}>*</span></label>
                  <input type="text" className="form-control form-control-sm" value={groupId} onChange={(e) => setGroupId(e.target.value)} />
                </div>
                <div className="col-6">
                  <label className="small">Set Type <span style={{ color: "red" }}>*</span></label>
                  <input type="text" className="form-control form-control-sm" value={setType} onChange={(e) => setSetType(e.target.value)} />
                </div>
              </div>
              <Button variant="secondary" className="w-100 mb-3" onClick={handleSetDevicesByGroup}>Set Devices by Group</Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FranklinUnifiedDetails;
